<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useForm } from '@/composables/useForm'

const { t } = useI18n()
const router = useRouter()
const { login } = useAdminAuth()

const { fields, errors, setErrors, resetErrors } = useForm({
  email: '',
  password: '',
})

const loading = ref(false)
const credentialError = ref<string | null>(null)

async function handleSubmit(): Promise<void> {
  resetErrors()
  credentialError.value = null

  // Basic client-side validation
  const clientErrors: Record<string, string[]> = {}
  if (!fields.email) {
    clientErrors.email = [t('admin.auth.email') + ' is required']
  }
  if (!fields.password) {
    clientErrors.password = [t('admin.auth.password') + ' is required']
  }
  if (Object.keys(clientErrors).length > 0) {
    setErrors(clientErrors)
    return
  }

  loading.value = true
  try {
    await login(fields.email as string, fields.password as string)
    await router.push({ name: 'admin.dashboard' })
  } catch (e: unknown) {
    if (isStatusError(e, 401)) {
      credentialError.value = t('admin.auth.invalid_credentials')
    } else if (is422Error(e)) {
      const errResp = (e as { response: { data: { errors: Record<string, string[]> } } }).response.data.errors
      setErrors(errResp)
    } else {
      credentialError.value = t('admin.common.error_generic')
    }
  } finally {
    loading.value = false
  }
}

function isStatusError(e: unknown, status: number): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    (e as { response: { status: number } }).response.status === status
  )
}

function is422Error(e: unknown): boolean {
  return isStatusError(e, 422)
}
</script>

<template>
  <div class="min-h-screen bg-slate-950 flex items-center justify-center px-4">
    <div class="w-full max-w-sm">
      <div class="text-center mb-8">
        <h1 class="text-2xl font-bold text-white">{{ t('admin.auth.title') }}</h1>
        <p class="text-slate-400 text-sm mt-1">{{ t('admin.auth.subtitle') }}</p>
      </div>

      <form
        class="bg-slate-900 border border-slate-700 rounded-2xl p-6 space-y-4"
        novalidate
        @submit.prevent="handleSubmit"
      >
        <!-- Credential error banner -->
        <div
          v-if="credentialError"
          class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300"
          role="alert"
        >
          {{ credentialError }}
        </div>

        <!-- Email field -->
        <div>
          <label for="email" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('admin.auth.email') }}
          </label>
          <input
            id="email"
            v-model="fields.email"
            type="email"
            autocomplete="email"
            :placeholder="t('admin.auth.email_placeholder')"
            class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors.email ? 'border-red-500' : 'border-slate-600'"
          />
          <p v-if="errors.email" class="mt-1 text-xs text-red-400">{{ errors.email[0] }}</p>
        </div>

        <!-- Password field -->
        <div>
          <label for="password" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('admin.auth.password') }}
          </label>
          <input
            id="password"
            v-model="fields.password"
            type="password"
            autocomplete="current-password"
            :placeholder="t('admin.auth.password_placeholder')"
            class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
            :class="errors.password ? 'border-red-500' : 'border-slate-600'"
          />
          <p v-if="errors.password" class="mt-1 text-xs text-red-400">{{ errors.password[0] }}</p>
        </div>

        <!-- Submit button -->
        <button
          type="submit"
          :disabled="loading"
          class="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors flex items-center justify-center gap-2"
        >
          <svg v-if="loading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {{ loading ? t('admin.auth.signing_in') : t('admin.auth.sign_in') }}
        </button>
      </form>
    </div>
  </div>
</template>
